import { forwardRef, type ReactNode } from "react";
import { motion, type HTMLMotionProps } from "framer-motion";
import { Loader2, type LucideIcon } from "lucide-react";

// ─────────────────────────────────────────────────────────────────
// Iratation High School OS — buttons.
//
// Four variants, matching the four roles a button plays in iOS:
//
//   primary   — filled with the brand color. One per screen.
//   secondary — filled with a light neutral. Two or three per screen.
//   ghost     — text-only, blue. Links and tertiary actions.
//   danger    — filled red. Destructive confirmations.
//
// Three sizes: sm (28), md (36), lg (48). The md size is what
// every form button uses; lg is only for full-width CTAs on the
// login and customize screens.
//
// The press effect is a scale-down plus an opacity dip, both
// driven by Framer Motion's `whileTap` rather than CSS `:active`.
// Why motion instead of `:active`? Because CSS can't express the
// spring-back after release — it snaps. iOS buttons settle.
// ─────────────────────────────────────────────────────────────────

type Variant = "primary" | "secondary" | "ghost" | "danger";
type Size = "sm" | "md" | "lg";

type ButtonProps = {
  children: ReactNode;
  variant?: Variant;
  size?: Size;
  /** Icon rendered before the label. */
  leadingIcon?: LucideIcon;
  /** Icon rendered after the label. */
  trailingIcon?: LucideIcon;
  /** Show a spinner and disable interaction. */
  loading?: boolean;
  /** Stretch to fill the parent's width. */
  fullWidth?: boolean;
} & Omit<HTMLMotionProps<"button">, "children">;

// ─── Style maps ─────────────────────────────────────────────────
//
// Kept as plain objects, not Tailwind class strings, because the
// primary variant needs to read `var(--brand-navy)` and mixing
// that into a Tailwind class would require arbitrary-value syntax
// (`bg-[var(--brand-navy)]`) which is noisier and less scannable.

const VARIANTS: Record<
  Variant,
  { bg: string; fg: string; border?: string; shadow?: string }
> = {
  primary: {
    bg: "var(--brand-navy)",
    fg: "#FFFFFF",
    shadow: "0 4px 14px rgba(10, 37, 64, 0.24)",
  },
  secondary: {
    bg: "rgba(120, 120, 128, 0.12)",
    fg: "#007AFF",
  },
  ghost: {
    bg: "transparent",
    fg: "#007AFF",
  },
  danger: {
    bg: "#FF3B30",
    fg: "#FFFFFF",
    shadow: "0 4px 14px rgba(255, 59, 48, 0.24)",
  },
};

const SIZES: Record<
  Size,
  { h: string; px: string; text: string; gap: string; iconSize: number; radius: string }
> = {
  sm: { h: "h-7",  px: "px-2.5", text: "text-ios-footnote", gap: "gap-1",   iconSize: 14, radius: "rounded-ios-md" },
  md: { h: "h-9",  px: "px-4",   text: "text-ios-subhead", gap: "gap-1.5", iconSize: 16, radius: "rounded-ios-md" },
  lg: { h: "h-12", px: "px-6",   text: "text-ios-body",    gap: "gap-2",   iconSize: 18, radius: "rounded-ios-lg" },
};

// ─── Component ──────────────────────────────────────────────────

const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    children,
    variant = "primary",
    size = "md",
    leadingIcon: Leading,
    trailingIcon: Trailing,
    loading = false,
    fullWidth = false,
    disabled,
    className = "",
    ...rest
  },
  ref
) {
  const v = VARIANTS[variant];
  const s = SIZES[size];
  const isDisabled = disabled || loading;

  return (
    <motion.button
      ref={ref}
      type="button"
      disabled={isDisabled}
      // whileTap drives the press animation. scale 0.97 is
      // enough to feel responsive without being cartoonish.
      // Combined with the opacity dip, this is the same
      // feedback iOS gives on a filled button.
      whileTap={isDisabled ? undefined : { scale: 0.97, opacity: 0.9 }}
      transition={{ type: "spring", stiffness: 500, damping: 30, mass: 0.6 }}
      className={[
        "relative inline-flex items-center justify-center font-semibold select-none",
        s.h,
        s.px,
        s.text,
        s.gap,
        s.radius,
        "focus-ring",
        fullWidth ? "w-full" : "",
        isDisabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      style={{
        backgroundColor: v.bg,
        color: v.fg,
        boxShadow: v.shadow,
      }}
      {...rest}
    >
      {loading ? (
        <Loader2
          size={s.iconSize}
          strokeWidth={2.4}
          className="animate-spin"
          absoluteStrokeWidth
        />
      ) : (
        Leading && (
          <Leading size={s.iconSize} strokeWidth={2.4} absoluteStrokeWidth />
        )
      )}

      <span className="truncate">{children}</span>

      {!loading && Trailing && (
        <Trailing size={s.iconSize} strokeWidth={2.4} absoluteStrokeWidth />
      )}
    </motion.button>
  );
});

export default Button;